import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FileText, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { formatErr } from './shared';

// Same box the PDF header reserves (billing_pdf._company_block): 28 mm wide, 28/1.85 mm tall, times the scale.
const BOX_W = 28;
const BOX_H = 28 / 1.85;
const PX = 2.6; // preview pixels per mm

const fit = (aspect, k) => {
  const w = Math.min(BOX_W * k, BOX_H * k * aspect);
  return { w: w * PX, h: (w / aspect) * PX };
};

const HeaderMock = ({ src, aspect, scale, docType, company }) => {
  const { w, h } = fit(aspect || 1.85, scale);
  return (
    <div className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm" style={{ width: 210 * PX * 0.48 }} data-testid={`logo-mock-${docType.toLowerCase()}`}>
      <div className="flex justify-between gap-3" style={{ padding: `${10 * PX * 0.48}px ${12 * PX * 0.48}px` }}>
        <div className="min-w-0">
          {src ? <img src={src} alt="" style={{ width: w * 0.48, height: h * 0.48 }} className="object-contain transition-[width,height] duration-150" />
            : <div className="grid place-items-center rounded bg-slate-100 text-[10px] text-slate-400" style={{ width: w * 0.48, height: h * 0.48 }}>Logo</div>}
          <div className="mt-1 truncate text-[11px] font-bold text-slate-900">{company || 'Your workshop'}</div>
          <div className="h-1 w-24 rounded bg-slate-200" /><div className="mt-0.5 h-1 w-16 rounded bg-slate-200" />
        </div>
        <div className="text-right">
          <div className="text-base font-bold text-slate-900">{docType}</div>
          <div className="text-[9px] font-semibold text-slate-500"># SAMPLE-0001</div>
        </div>
      </div>
      <div className="mx-3 mb-3 space-y-1 border-t border-slate-100 pt-2">
        <div className="h-1.5 w-20 rounded bg-slate-200" /><div className="h-1 w-32 rounded bg-slate-100" /><div className="h-1 w-28 rounded bg-slate-100" />
      </div>
    </div>
  );
};

// Logo size slider with a live invoice/estimate header preview and a real sample PDF.
export const LogoSizePreview = ({ version, company }) => {
  const { authHeader } = useAuth();
  const [meta, setMeta] = useState(null);
  const [scale, setScale] = useState(1);
  const [src, setSrc] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/billing/settings/logo-meta`, { headers: authHeader() });
    setMeta(data); setScale(data.scale || 1);
    if (!data.prints_logo) { setSrc(''); return; }
    const res = await axios.get(`${API}/billing/settings/logo-effective`, { headers: authHeader(), responseType: 'blob' });
    setSrc((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(res.data); });
  }, [authHeader]);
  useEffect(() => { load().catch(() => setMeta(null)); }, [load, version]);

  const saved = meta && Math.abs((meta.scale || 1) - scale) < 0.001;
  const pct = useMemo(() => Math.round(scale * 100), [scale]);

  const save = async () => {
    setBusy('save');
    try {
      const { data } = await axios.put(`${API}/billing/settings/logo-scale`, { scale }, { headers: authHeader() });
      setMeta((m) => ({ ...m, scale: data.scale }));
      toast.success(`Logo size saved at ${Math.round(data.scale * 100)}%. New PDFs use it right away.`);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(''); }
  };

  const sample = async (docType) => {
    setBusy(docType);
    const tab = window.open('', '_blank'); // open inside the click so Safari allows it
    try {
      const res = await axios.get(`${API}/billing/settings/sample-invoice`, { params: { scale, doc_type: docType }, headers: authHeader(), responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      if (tab) tab.location = url; else window.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { if (tab) tab.close(); toast.error(formatErr(e)); } finally { setBusy(''); }
  };

  if (!meta) return null;
  return (
    <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50/60 p-4" data-testid="logo-size-panel">
      <div className="flex flex-wrap items-center gap-3">
        <label htmlFor="logo-scale" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Logo size</label>
        <input id="logo-scale" type="range" min={meta.min} max={meta.max} step={0.05} value={scale} disabled={!meta.prints_logo}
          onChange={(e) => setScale(Number(e.target.value))} className="w-56 accent-teal-600" data-testid="logo-scale-slider" />
        <span className="w-12 text-sm font-semibold tabular-nums text-slate-800" data-testid="logo-scale-value">{pct}%</span>
        <button type="button" onClick={() => setScale(1)} disabled={scale === 1} className="text-xs text-slate-500 underline-offset-2 hover:underline disabled:opacity-40" data-testid="logo-scale-reset">Reset</button>
        <button type="button" onClick={save} disabled={saved || !!busy} className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-teal-700 disabled:opacity-50" data-testid="logo-scale-save">
          {busy === 'save' ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {saved ? 'Saved' : 'Save size'}
        </button>
      </div>
      {!meta.prints_logo && <p className="text-xs text-slate-500" data-testid="logo-scale-no-logo">Upload a logo to adjust its size.</p>}
      <div className="flex flex-wrap gap-4">
        {['Invoice', 'Estimate'].map((t) => (
          <div key={t} className="space-y-1.5">
            <HeaderMock src={src} aspect={meta.aspect} scale={scale} docType={t} company={company} />
            <button type="button" onClick={() => sample(t)} disabled={!!busy} className="inline-flex items-center gap-1 text-xs font-semibold text-teal-700 hover:underline disabled:opacity-50" data-testid={`logo-sample-${t.toLowerCase()}`}>
              {busy === t ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />} Open sample {t.toLowerCase()} PDF at {pct}%
            </button>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-500">Empty margins around your logo are trimmed when you upload it. The size applies to the header of invoices, estimates and GST invoices.</p>
    </div>
  );
};
